import { useEffect, useRef, useState } from "react";
import useMediaQuery from "../../../hooks/useMediaQuery";
import { MEDIA } from "../../../styles/breakpoints";
import { connect } from 'react-redux';
import { get_categorias } from "../../../redux/actions/categorias";
import { MdCategory, MdCheck, MdChevronRight, MdClose, MdExpandLess, MdExpandMore } from "react-icons/md";
import Portal from "../../ui/Portal";
import styles from './CategoryList.module.css';

// headerAction (opcional): botón extra junto a las categorías, ej. "Nuevo producto" del inventario.
// Escritorio: en la cabecera del panel. Móvil y tablet: al lado del botón de categorías.
const CategoryList = ({ get_categorias, categorias, mostrarPorCategoria, mostrarPorEquipo, headerAction }) => {
    useEffect(() => {
        get_categorias();
    }, [get_categorias]);

    const categoryRef = useRef(null);
    const closeButtonRef = useRef(null);
    const [showSecciones, setShowSecciones] = useState(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [selectedSeccion, setSelectedSeccion] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Móvil y tablet (< 1280px, como el sidebar hamburguesa): botón + panel lateral. Escritorio: panel fijo.
    const isCompact = useMediaQuery(MEDIA.tabletDown);

    // Escritorio: un clic fuera del panel cierra las secciones y limpia la selección. En compacto no
    // corre: el panel lateral está en un Portal (fuera de categoryRef) y el botón muestra la selección.
    useEffect(() => {
        if (isCompact) return undefined;
        const handleClickOutside = (event) => {
            if (categoryRef.current && !categoryRef.current.contains(event.target)) {
                setShowSecciones(false);
                setCurrentCategory(null);
                setSelectedSeccion(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isCompact]);

    // Panel lateral: Escape cierra y el foco va al botón de cerrar al abrir.
    useEffect(() => {
        if (!drawerOpen) return undefined;
        closeButtonRef.current?.focus();
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setDrawerOpen(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [drawerOpen]);

    useEffect(() => {
        if (!isCompact) setDrawerOpen(false);
    }, [isCompact]);

    const handleCategoryClick = (categoria) => {
        if (categoria === "Todo") {
            setCurrentCategory(null);
            setSelectedSeccion(null);
            setShowSecciones(false);
            mostrarPorCategoria("Todo");
            setDrawerOpen(false);
        } else {
            if (categoria.secciones && categoria.secciones.length > 0) {
                setCurrentCategory(categoria);
                setShowSecciones(true);
                setSelectedSeccion(null);
                mostrarPorCategoria(categoria.nombre);
            } else {
                setCurrentCategory(categoria);
                setShowSecciones(false);
                setSelectedSeccion(null);
                mostrarPorCategoria(categoria.nombre);
                setDrawerOpen(false);
            }
        }
    };

    const handleSeccionClick = (seccion, event) => {
        event.stopPropagation();
        setSelectedSeccion(seccion);
        mostrarPorCategoria(currentCategory.nombre, seccion.nombre);
        setDrawerOpen(false);
    };

    // "Todas" las secciones de la categoría abierta
    const handleAllSeccionesClick = () => {
        setSelectedSeccion(null);
        mostrarPorCategoria(currentCategory.nombre);
        setDrawerOpen(false);
    };

    if (isCompact) {
        const selectionLabel = currentCategory
            ? `${currentCategory.nombre}${selectedSeccion ? ` › ${selectedSeccion.nombre}` : ''}`
            : 'Todo';

        const trigger = (
            <button
                type="button"
                className={styles.trigger}
                onClick={() => setDrawerOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={drawerOpen}
            >
                <MdCategory aria-hidden="true" className={styles.triggerIcon} />
                <span className={styles.triggerLabel}>Categorías:</span>
                <span className={styles.triggerValue}>{selectionLabel}</span>
            </button>
        );

        return (
            <>
                {headerAction ? (
                    <div className={styles.toolbar}>
                        {trigger}
                        {headerAction}
                    </div>
                ) : trigger}
                {drawerOpen && (
                    <Portal>
                        <div className={styles.drawerBackdrop} onClick={() => setDrawerOpen(false)}>
                            <div
                                className={styles.drawer}
                                role="dialog"
                                aria-modal="true"
                                aria-label="Categorías"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <div className={styles.header}>
                                    <h2 className={styles.heading}>Categorías</h2>
                                    <button
                                        type="button"
                                        ref={closeButtonRef}
                                        className={styles.closeBtn}
                                        onClick={() => setDrawerOpen(false)}
                                        aria-label="Cerrar categorías"
                                    >
                                        <MdClose aria-hidden="true" />
                                    </button>
                                </div>
                                <ul className={styles.optionList}>
                                    <li>
                                        <button
                                            type="button"
                                            className={`${styles.option} ${!currentCategory ? styles.optionActive : ''}`}
                                            onClick={() => handleCategoryClick("Todo")}
                                        >
                                            Todo
                                            {!currentCategory && <MdCheck aria-hidden="true" />}
                                        </button>
                                    </li>
                                    {categorias && categorias.map(categoria => {
                                        const isActive = currentCategory && currentCategory.id === categoria.id;
                                        const hasSecciones = categoria.secciones && categoria.secciones.length > 0;
                                        const isExpanded = isActive && showSecciones && hasSecciones;
                                        return (
                                            <li key={categoria.id}>
                                                <button
                                                    type="button"
                                                    className={`${styles.option} ${isActive ? styles.optionActive : ''}`}
                                                    onClick={() => handleCategoryClick(categoria)}
                                                    aria-expanded={hasSecciones ? isExpanded : undefined}
                                                >
                                                    {categoria.nombre}
                                                    {hasSecciones
                                                        ? (isExpanded ? <MdExpandLess aria-hidden="true" /> : <MdExpandMore aria-hidden="true" />)
                                                        : (isActive && <MdCheck aria-hidden="true" />)}
                                                </button>
                                                {isExpanded && (
                                                    <ul className={styles.optionSubList}>
                                                        <li>
                                                            <button
                                                                type="button"
                                                                className={`${styles.subOption} ${!selectedSeccion ? styles.optionActive : ''}`}
                                                                onClick={handleAllSeccionesClick}
                                                            >
                                                                Todas
                                                                {!selectedSeccion && <MdCheck aria-hidden="true" />}
                                                            </button>
                                                        </li>
                                                        {categoria.secciones.map(seccion => {
                                                            const isSeccionActive = selectedSeccion && selectedSeccion.id === seccion.id;
                                                            return (
                                                                <li key={seccion.id}>
                                                                    <button
                                                                        type="button"
                                                                        className={`${styles.subOption} ${isSeccionActive ? styles.optionActive : ''}`}
                                                                        onClick={(event) => handleSeccionClick(seccion, event)}
                                                                    >
                                                                        {seccion.nombre}
                                                                        {isSeccionActive && <MdCheck aria-hidden="true" />}
                                                                    </button>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>
                    </Portal>
                )}
            </>
        );
    }

    // Escritorio: panel fijo a la izquierda; las secciones de la categoría elegida se abren en un
    // segundo panel a la derecha. Mismo estilo de opciones que el panel lateral de móvil/tablet.
    return (
        <nav ref={categoryRef} className={styles.categorias} aria-label="Categorías">
            <div className={styles.card}>
                <div className={styles.header}>
                    <h2 className={styles.heading}>Categorías</h2>
                    {headerAction}
                </div>
                <ul className={styles.optionList}>
                    <li>
                        <button
                            type="button"
                            className={`${styles.option} ${!currentCategory ? styles.optionActive : ''}`}
                            onClick={() => handleCategoryClick("Todo")}
                            aria-current={!currentCategory ? 'true' : undefined}
                        >
                            Todo
                            {!currentCategory && <MdCheck aria-hidden="true" />}
                        </button>
                    </li>
                    {categorias && categorias.map(categoria => {
                        const isActive = currentCategory && currentCategory.id === categoria.id;
                        const hasSecciones = categoria.secciones && categoria.secciones.length > 0;
                        return (
                            <li key={categoria.id}>
                                <button
                                    type="button"
                                    className={`${styles.option} ${isActive ? styles.optionActive : ''}`}
                                    onClick={() => handleCategoryClick(categoria)}
                                    aria-current={isActive ? 'true' : undefined}
                                    aria-expanded={hasSecciones ? Boolean(isActive && showSecciones) : undefined}
                                >
                                    {categoria.nombre}
                                    {hasSecciones
                                        ? <MdChevronRight aria-hidden="true" />
                                        : (isActive && <MdCheck aria-hidden="true" />)}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </div>
            {showSecciones && currentCategory && currentCategory.secciones && (
                <div className={`${styles.card} ${styles.subCard}`}>
                    <div className={styles.header}>
                        <h2 className={`${styles.heading} ${styles.subHeading}`}>{currentCategory.nombre}</h2>
                        <button
                            type="button"
                            className={styles.closeBtn}
                            onClick={() => setShowSecciones(false)}
                            aria-label="Cerrar secciones"
                        >
                            <MdClose aria-hidden="true" />
                        </button>
                    </div>
                    <ul className={styles.optionList}>
                        <li>
                            <button
                                type="button"
                                className={`${styles.subOption} ${!selectedSeccion ? styles.optionActive : ''}`}
                                onClick={handleAllSeccionesClick}
                                aria-current={!selectedSeccion ? 'true' : undefined}
                            >
                                Todas
                                {!selectedSeccion && <MdCheck aria-hidden="true" />}
                            </button>
                        </li>
                        {currentCategory.secciones.map(seccion => {
                            const isSeccionActive = selectedSeccion && selectedSeccion.id === seccion.id;
                            return (
                                <li key={seccion.id}>
                                    <button
                                        type="button"
                                        className={`${styles.subOption} ${isSeccionActive ? styles.optionActive : ''}`}
                                        onClick={(event) => handleSeccionClick(seccion, event)}
                                        aria-current={isSeccionActive ? 'true' : undefined}
                                    >
                                        {seccion.nombre}
                                        {isSeccionActive && <MdCheck aria-hidden="true" />}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </nav>
    );
};

const mapStateToProps = state => ({
    categorias: state.Categorias.categorias,
});

export default connect(mapStateToProps, {
    get_categorias,
})(CategoryList);
